import InspectOuter from 'app/components/cars/InspectOuter';
import ReportInspectImage from 'app/components/cars/ReportInspectImage';
import Button from 'app/components/common/Button';
import { BaseVideoPlayer } from '@encarpkg/design';
import useCars from 'app/hook/cars/useCars';
import useAccordion from 'app/hook/common/useAccordion';
import dayjs from 'app/libs/dayjs';
import { numberWithCommas } from 'app/libs/number';
import classNames from 'classnames/bind';
import React, { memo, useEffect, useState } from 'react';
import styles from './ReportInspectList.module.scss';

const cx = classNames.bind(styles);
const imgServerURL = process.env.REACT_APP_IMG_SERVER;

function ReportInspectList({ type = '' }) {
	const { base, inspect } = useCars();
	const { master, images, outers, inners, price, etcs, inspectionSource } = inspect;
	const { setAccordionLength, accordionArray, accordionStatsAll, handleAccordionMulti, handleAccordionAll } = useAccordion();

	const baseVehicleId = base.vehicleId;

	const innerIndex = inners.length + 1 + (etcs && 1) + (price && 1);
	const [inspectorDropDown, setInspectorDropDwon] = useState(false);
	useEffect(() => {
		setAccordionLength(innerIndex);
	}, [setAccordionLength, inners, innerIndex]);

	// 성능 버전
	const v2 = master.detail?.version === 'V180701';
	// const v3 = master.detail?.version === 'V200922';

	// 가격조사 산정액
	const findPrice = (code) => price?.priceDetail.find((n) => n.type.code === code);
	const tagPrice = (code) => {
		const item = findPrice(code);
		return item.price > 0 ? (
			<dl className={cx('info')}>
				<dt>가격조사 산정액</dt>
				<dd>{numberWithCommas(item.price)}만원</dd>
				{item.description && (
					<>
						<dt>특기사항</dt>
						<dd>{item.description}</dd>
					</>
				)}
			</dl>
		) : null;
	};

	// 자동차 세부상태
	const tagInners = inners.map((item, i) => (
		<li
			key={item.type.code}
			className={cx('none', { on: accordionArray[i] })}
		>
			<button
				type="button"
				className={cx('toggle')}
				onClick={() => handleAccordionMulti(i)}
			>
				<span className={cx('tit', { check: item.price })}>{item.type.title}</span>
				{item.price > 0 && <span className={cx('price')}>가격조사 산정액: {numberWithCommas(item.price)}만원</span>}
			</button>
			<ul className={cx('sub', { on: accordionArray[i] })}>
				{item.children.map((subItem) => {
					const subItemFilter = subItem.children;
					const value =
						subItemFilter.length > 0 ? (
							subItemFilter.map((deps3) => (
								<li key={deps3.type.code}>
									<span className={cx('tit')}>
										{subItem.type.title} - {deps3.type.title}
									</span>
									<span className={cx('txt')}>{deps3.statusType?.title || '-'}</span>
									{deps3.description && (
										<dl className={cx('info')}>
											<dt>특기사항</dt>
											<dd>{deps3.description}</dd>
										</dl>
									)}
								</li>
							))
						) : (
							<li key={subItem.type.code}>
								<span className={cx('tit')}>{subItem.type.title}</span>
								<span className={cx('txt')}>{subItem.statusType?.title || '-'}</span>
								{subItem.description && (
									<dl className={cx('info')}>
										<dt>특기사항</dt>
										<dd>{subItem.description}</dd>
									</dl>
								)}
							</li>
						);
					return value;
				})}
			</ul>
		</li>
	));
	// 자동차 기타정보
	const etcItemExists = v2 ? ['있음', '없음'] : ['양호', '불량'];
	const tagEtcs = () => {
		const indexNumber = inners.length;
		const etcsPrice = findPrice('E01') || {};
		return (
			<li className={cx('none', { on: accordionArray[indexNumber] })}>
				<button
					type="button"
					className={cx('toggle')}
					onClick={() => handleAccordionMulti(indexNumber)}
				>
					<span className={cx('tit', { check: etcsPrice.price })}>자동차 기타정보</span>
					{etcsPrice.price && <span className={cx('price')}>가격조사 산정액: {numberWithCommas(etcsPrice.price)}만원</span>}
				</button>
				<ul className={cx('sub', { on: accordionArray[indexNumber] })}>
					{etcs.map((item) => {
						const value =
							item.children.length > 0 &&
							item.children.map((subItem) => {
								const result = (subItem.exists === null && '-') || (subItem.exists ? etcItemExists[0] : etcItemExists[1]);
								return (
									<li key={subItem.type.code}>
										<span className={cx('tit')}>
											{item.type.title} - {subItem.type.title}
										</span>
										<span className={cx('txt')}>
											{result}
											{subItem.statusTypes.length > 0 && (
												<>
													(
													{subItem.statusTypes.map((v, i) => (
														<span key={v.code}>
															{i !== 0 && ','}
															{v.title}
														</span>
													))}
													)
												</>
											)}
										</span>
									</li>
								);
							});
						return value;
					})}
				</ul>
			</li>
		);
	};
	// 특기사항 및 점검자의 의견
	const tagPriceComments = () => {
		const indexNumber = inners.length + (etcs && 1);
		return (
			<li className={cx('none', { on: accordionArray[indexNumber] })}>
				<button
					type="button"
					className={cx('toggle')}
					onClick={() => handleAccordionMulti(indexNumber)}
				>
					<span className={cx('tit')}>특기사항 및 점검자의 의견</span>
				</button>
				<ul className={cx('sub', { on: accordionArray[indexNumber] })}>
					<li>
						<span className={cx('tit')}>성능 상태점검자</span>
						<span className={cx('txt')}>{master.detail.comments || '-'}</span>
					</li>
					<li>
						<span className={cx('tit')}>가격 조사산정자</span>
						<span className={cx('txt')}>{price?.priceComments || '-'}</span>
					</li>
				</ul>
			</li>
		);
	};

	// 점검 장면 촬영 사진
	const tagImages = (code) => {
		const item = images.filter((v) => v.type.indexOf(code) !== -1);
		const tags = item.map((v) => (
			<li key={v.type}>
				<img
					src={`${imgServerURL}${v.path}`}
					className={cx('thumb')}
					alt=""
				/>
				{v.title}
			</li>
		));
		return tags;
	};

	// 직영성능장
	const isDirectManagement = inspectionSource?.code === 'DIRECT';

	return (
		<>
			{isDirectManagement && (
				<div className={cx('inspect_specialize')}>
					<div className={cx('inspect_complete_box')}>
						<h4 className={cx('tit_specialize')}>
							<span className={cx('logo')}>Encar 직영성능점검차량</span>
						</h4>
						<p className={cx('tit_image')}>
							이 차량은 <b>엔카가 직접 점검</b>했어요! 엔카 직영 성능점검장에서는 <b>69개의 항목을 꼼꼼히 점검</b>합니다.
						</p>
						<div className={cx('video_box')}>
							<div className={cx('inner_box')}>
								<BaseVideoPlayer
									onReady={(videoPlayer) => {
										const videoList = [
											'https://dhspiemp944fp.cloudfront.net/direct_inspection_a/Default/HLS/direct_inspection_a_360.m3u8',
											'https://dhspiemp944fp.cloudfront.net/direct_inspection_b/Default/HLS/direct_inspection_b_360.m3u8',
											'https://dhspiemp944fp.cloudfront.net/direct_inspection_c/Default/HLS/direct_inspection_c_360.m3u8',
											'https://dhspiemp944fp.cloudfront.net/direct_inspection_d/Default/HLS/direct_inspection_d_360.m3u8',
										];
										videoPlayer.src({
											src: videoList[Math.floor(Math.random() * videoList.length)],
											type: 'application/x-mpegURL',
										});
									}}
									aspectRatio="16:9"
								/>
							</div>
						</div>
						<button
							type="button"
							className={cx('inspector_btn_box', { on: inspectorDropDown })}
							onClick={() => setInspectorDropDwon((prev) => !prev)}
						>
							<em className={cx('txt_inspector')}>
								<b>성능 ・ 상태 점검자</b> : {master.detail.inspName || '-'}
							</em>
							{inspectorDropDown && (
								<em className={cx('txt_inspector')}>
									<b>성능 ・ 상태 고지자</b> : {master.detail.noticeName || '-'}
								</em>
							)}
						</button>
					</div>
					<ul className={cx('info_specialize')}>
						<li>엔카는 차량 품질 관리를 위해 성능점검장을 직접 운영하고 있습니다.</li>
						<li>엔카 성능점검 규정은 자동차 관리법의 규정을 따르며 법적 보증 기간 1개월, 2,000km 보증이 가능합니다. (보험사 보증)</li>
					</ul>
				</div>
			)}
			{master.detail ? (
				<>
					<div className={cx('car_inspect', { ohmycall: type === 'ohmycall' })}>
						<strong className={cx('txt_supply')}>제시번호 {master.supplyNum}</strong>
					</div>
					<h4 className={cx('blind')}>사고, 교환, 수리 등 이력</h4>
					<div className={cx('visual_inspect')}>
						<InspectOuter data={outers} />
					</div>
					<h4 className={cx('blind')}>성능점검 기록부</h4>
					<strong className={cx('info_inspect')}>
						제 {master.detail.recordNo}호 (최초등록일 {dayjs(master.detail.firstRegistrationDate).format('YYYY/MM/DD')})
					</strong>
					<ul className={cx('list_inspect')}>
						<li>
							<span className={cx('tit')}>변속기 종류</span>
							<span className={cx('txt')}>{master.detail.transmissionType?.title || '-'}</span>
						</li>
						<li>
							<span className={cx('tit')}>사용연료</span>
							<span className={cx('txt')}>{base.spec.fuelName || '-'}</span>
						</li>
						<li>
							<span className={cx('tit')}>보증유형</span>
							<span className={cx('txt')}>{master.detail.guarantyType.title || '-'}</span>
						</li>
						<li>
							<span className={cx('tit')}>원동기 형식</span>
							<span className={cx('txt')}>{master.detail.motorType || '-'}</span>
						</li>
						{price?.priceCondition && (
							<li>
								<span className={cx('tit')}>가격산정 기준가격</span>
								<span className={cx('txt')}>{`${numberWithCommas(price.priceCondition)}만원` || '-'}</span>
							</li>
						)}
					</ul>
					<strong className={cx('info_inspect')}>자동차 종합상태</strong>
					<ul className={cx('list_inspect')}>
						<li>
							<span className={cx('tit')}>주행거리 계기상태</span>
							<span className={cx('txt')}>{master.detail.boardStateType?.title || '-'}</span>
							{price && tagPrice('M001')}
						</li>
						<li>
							<span className={cx('tit')}>주행거리 상태</span>
							<span className={cx('txt')}>
								{master.detail.mileageStateType && `${master.detail.mileageStateType.title} / `}
								{numberWithCommas(master.detail.mileage)}km
							</span>
							{price && tagPrice('M002')}
						</li>
						<li>
							<span className={cx('tit')}>차대번호표기</span>
							<span className={cx('txt')}>{master.detail.carStateType.title}</span>
							{price && tagPrice('M003')}
						</li>
						<li>
							<span className={cx('tit')}>튜닝</span>
							<span className={cx('txt')}>
								{master.detail.tuningStateTypes.length > 0 ? '있음' : '없음'}
								{master.detail.tuningStateTypes.map((v) => (
									<span key={v.code}> / {v.title}</span>
								))}
							</span>
							{price && tagPrice('M005')}
						</li>
						<li>
							<span className={cx('tit')}>특별이력</span>
							<span className={cx('txt')}>
								{master.detail.seriousTypes.length > 0 ? '있음' : '없음'}
								{master.detail.seriousTypes.map((v) => (
									<span key={v.code}> / {v.title}</span>
								))}
							</span>
							{price && tagPrice('M006')}
						</li>
						<li>
							<span className={cx('tit')}>용도변경</span>
							<span className={cx('txt')}>
								{master.detail.usageChangeTypes.length > 0 ? '있음' : '없음'}
								{master.detail.usageChangeTypes.map((v) => (
									<span key={v.code}> / {v.title}</span>
								))}
							</span>
							{price && tagPrice('M007')}
						</li>
						<li>
							<span className={cx('tit')}>배출가스</span>
							<span className={cx('txt')}>
								{base.spec.fuelCd === '002' ? (
									<>매연: {numberWithCommas(master.detail.smout || '-')}%</>
								) : (
									<>
										일산화탄소: {numberWithCommas(master.detail.coout || '-')}% / 탄화수소:{' '}
										{numberWithCommas(master.detail.hcout || '-')}ppm
									</>
								)}
							</span>
							{price && tagPrice('M004')}
						</li>
						{(findPrice('M008')?.price || findPrice('M008')?.description) && (
							<li>
								<span className={cx('tit')}>색상</span>
								<span className={cx('txt')}>
									{master.detail?.colorType?.title || '-'}
									{master.detail.paintPartTypes.map((v) => (
										<span key={v.code}> / {v.title}</span>
									))}
								</span>
								{(findPrice('M008')?.price || findPrice('M008')?.description) && tagPrice('M008')}
							</li>
						)}
						{(findPrice('M009')?.price || findPrice('M009')?.description) && (
							<li>
								<span className={cx('tit')}>주요옵션</span>
								<span className={cx('txt')}>
									{master.detail.mainOptionTypes.length > 0 ? '있음' : '없음'}
									{master.detail.mainOptionTypes.map((v) => (
										<span key={v.code}> / {v.title}</span>
									))}
								</span>
								{(findPrice('M009')?.price || findPrice('M009')?.description) && tagPrice('M009')}
							</li>
						)}
						<li>
							<span className={cx('tit')}>리콜대상</span>
							<span className={cx('txt')}>
								{master.detail.recall === true ? '해당' : '해당없음'}
								{master.detail.recallFullFillTypes.map((v) => (
									<span key={v.code}> / {v.title}</span>
								))}
							</span>
						</li>
					</ul>
					<strong className={cx('info_inspect')}>사고 교환 수리 등 이력</strong>
					<ul className={cx('list_inspect')}>
						<li>
							<span className={cx('tit')}>사고이력</span>
							<span className={cx('txt')}>{master.accdient ? '있음' : '없음'}</span>
						</li>
						<li>
							<span className={cx('tit')}>단순수리</span>
							<span className={cx('txt')}>{master.simpleRepair ? '있음' : '없음'}</span>
						</li>
						{v2 && (
							<>
								<li>
									<span className={cx('tit')}>부위별 이상여부- 외판부위 1랭크</span>
									<span className={cx('txt')}>
										{outers.filter((v) => v.attributes && v.attributes.indexOf('RANK_ONE') !== -1).length ? '있음' : '없음'}
									</span>
								</li>
								<li>
									<span className={cx('tit')}>부위별 이상여부- 외판부위 2랭크</span>
									<span className={cx('txt')}>
										{outers.filter((v) => v.attributes && v.attributes.indexOf('RANK_TWO') !== -1).length ? '있음' : '없음'}
									</span>
								</li>
								<li>
									<span className={cx('tit')}>부위별 이상여부- 주요골격</span>
									<span className={cx('txt')}>
										{outers.filter(
											(v) =>
												v.attributes &&
												(v.attributes.indexOf('RANK_A') !== -1 ||
													v.attributes.indexOf('RANK_B') !== -1 ||
													v.attributes.indexOf('RANK_C') !== -1),
										).length
											? '있음'
											: '없음'}
										{outers.filter((v) => v.attributes && v.attributes.indexOf('RANK_A') !== -1).length ? ' / A랭크' : ''}
										{outers.filter((v) => v.attributes && v.attributes.indexOf('RANK_B') !== -1).length ? ' / B랭크' : ''}
										{outers.filter((v) => v.attributes && v.attributes.indexOf('RANK_C') !== -1).length ? ' / C랭크' : ''}
									</span>
								</li>
							</>
						)}
						{findPrice('A002') && (
							<li>
								<span className={cx('tit')}>가격조사 산정액 및 특기사항</span>
								{tagPrice('A002')}
							</li>
						)}
					</ul>
					<strong className={cx('info_inspect')}>
						자동차 세부상태
						<button
							type="button"
							className={cx('link')}
							onClick={() => handleAccordionAll()}
						>
							{accordionStatsAll ? '모두 펼쳐보기' : '모두 닫기'}
						</button>
					</strong>

					<ul className={cx('list_inspect')}>
						{tagInners}
						{(findPrice('E01')?.price || findPrice('E01')?.description) && tagEtcs()}
						{tagPriceComments()}
					</ul>
					{price?.totalPrice && (
						<div className={cx('result_inspect')}>
							<strong className={cx('tit')}>최종 가격조사 산정 금액: {numberWithCommas(price.totalPrice)}만원</strong>
							<p className={cx('desc')}>
								이 가격조사 산정가격은 {price.association === '1' ? '기술협회' : '한국자동차진단보증협회'}의 차량기준가액을 바탕으로
								한 기준가격과(기술사회)기준서를 적용하였음
							</p>
						</div>
					)}
					{images.filter((v) => v.type.indexOf('M') !== -1).length > 0 && (
						<>
							<strong className={cx('info_inspect')}>점검 장면 촬영 사진</strong>
							<ul className={cx('list_photo')}>{tagImages('M')}</ul>
						</>
					)}
					<div className={cx('notice_inspect')}>
						<p className={cx('desc')}>
							자동차 관리법 제58조 제1항 및 같은 법 시행규칙 제120조에 따라 중고자동차의 성능 상태를 점검, 자동차가격조사 산정하였음을
							확인합니다.
						</p>
						<p className={cx('date')}>{dayjs(master.detail.issueDate).format('YYYY년 MM월 DD일')}</p>
						<ul className={cx('list')}>
							<li>중고자동차 성능 상태 점검자: {master.detail.inspName} (인)</li>
							<li>자동차가격조사 산정자: {price?.priceNm || '-'} (인)</li>
							<li>중고자동차 성능 상태 고지자: {master.detail.noticeName} (인)</li>
						</ul>
						<p className={cx('notice')}>
							본 페이지는 중고자동차 성능 &middot; 상태 점검 기록부의 모든 항목을 동일하게 적용하고 있으며, 항목 위치는 일부
							변경되었습니다.
							<br />
							해당 내용은 판매자가 직접 입력한 내용으로 모든 책임은 판매자에게 있습니다.
						</p>
					</div>
					{images.filter((v) => v.type.indexOf('I') !== -1).length > 0 && (
						<div
							className="taCenter"
							style={{ margin: '10px 0' }}
						>
							<Button
								domType="anchor"
								title="성능점검기록부 사진"
								color="white"
								size="30"
								inline
								to={`/cars/report/inspect-image/${baseVehicleId}`}
							/>
						</div>
					)}
				</>
			) : (
				<ReportInspectImage />
			)}
		</>
	);
}

export default memo(ReportInspectList);
